/**
 * @engine/vfx — the serializable VFX graph document (+ inert legacy blocks).
 */
import type { BaseGraphData, ContextInstance } from '@engine/graph';
import type { VFXNodeInstance, VFXGraphVariable } from './nodes';
import type { VFXContextSettings } from './contexts';

/**
 * Context flow edge - connects context outputs to inputs.
 * Defines the execution flow: Spawn → Init → Update → Output
 */
export interface VFXContextFlowEdge {
  /** Unique edge ID */
  readonly id: string;
  
  /** Source context ID */
  readonly fromContextId: string;
  
  /** Source port ID (e.g., 'spawn_event', 'particle') */
  readonly fromPortId: string;
  
  /** Target context ID */
  readonly toContextId: string;
  
  /** Target port ID */
  readonly toPortId: string;
}

/**
 * VFX graph data - the serializable document.
 */
export interface VFXGraphData extends BaseGraphData<VFXNodeInstance, VFXGraphVariable> {
  /** Context instances */
  readonly contexts: readonly ContextInstance[];
  
  /** Context flow edges - defines execution flow between contexts */
  readonly contextFlowEdges?: readonly VFXContextFlowEdge[];
  
  /** Per-context settings (trigger configs, etc.) */
  readonly contextSettings?: VFXContextSettings;

  // NOTE: `capacity`/`simulation`/`rendering` below are INERT — never read by the
  // compiler/runtime (real capacity lives in `contextSettings`). Kept optional only so the
  // preset literals still type-check; `toVFXData()` no longer emits them and the serialized
  // `.vfx` document (VFXDocument) omits them entirely. Remove from presets in a later pass.
  readonly capacity?: {
    readonly maxParticles: number;
    readonly boundsMode: 'manual' | 'auto' | 'recorded';
    readonly bounds?: {
      readonly min: { x: number; y: number };
      readonly max: { x: number; y: number };
    };
  };
  readonly simulation?: {
    readonly space: 'local' | 'world';
    readonly deltaTimeMode: 'deltaTime' | 'smoothDeltaTime' | 'fixedDeltaTime';
    readonly fixedTimeStep?: number;
  };
  readonly rendering?: {
    readonly sortMode: 'none' | 'byAge' | 'byDistance';
    readonly blendMode: 'alpha' | 'additive' | 'multiply' | 'premultiplied';
    readonly castShadows?: boolean;
  };
}

/**
 * Serialized `.vfx` document — the on-disk file format (versioned envelope).
 *
 * The graph itself goes through the canonical `@engine/graph` serializer
 * (`SerializedGraph`: compact x/y + fn/fp edge keys + validation); VFX graph-level
 * data (contexts / flow edges / per-context settings) rides alongside. `contextId`
 * is carried on each serialized node via the serializer's `nodeExtras` hook.
 */
export interface VFXDocument {
  readonly version: number;
  readonly graph: import('@engine/graph').SerializedGraph;
  readonly contexts: readonly ContextInstance[];
  readonly contextFlowEdges: readonly VFXContextFlowEdge[];
  readonly contextSettings: VFXContextSettings;
}

/** Current `.vfx` document format version. */
export const VFX_DOC_VERSION = 1;
